import { useQuery } from '@apollo/client';
import { BillingAddressDetailsLine, ColGroup, SearchResultsTable, SimpleTD, TableBody, TableHead } from '@css-xp/base';
import InvoiceGroupIcon from '@css-xp/base/assets/img/icon-invoice-group.svg';
import { PaymentMethodSelectDetailsLine } from '@css-xp/base/common-tabs';
import {
  FetchInvoiceGroupsForTransactionAccountQuery,
  FetchInvoiceGroupsForTransactionAccountQueryVariables,
} from '@css-xp/graphtypes';
import { INVOICE_GROUP_PATH } from '@atlassian/cssxp-typescommon';
import React from 'react';
import { Link } from 'react-router-dom';
import { fetchInvoiceGroupsForTransactionAccount } from './queries/fetch-invoice-groups-for-transaction-account';
import { InvoiceGroupPurchaseOrder } from '@atlassian/cssxp-types-billing';

interface InvoiceGroupsTableProps {
  transactionAccountId?: string;
}

const columns = [
  {
    name: 'Name',
    customClassNames: 'col-20',
  },
  {
    name: 'Ship to address',
    customClassNames: 'col-30',
  },
  {
    name: 'Payment method',
    customClassNames: 'col-30',
  },
  {
    name: 'Currency',
    customClassNames: 'col-10',
  },
  {
    name: 'PO number',
    customClassNames: 'col-10',
  },
  {
    name: '',
  },
];

const InvoiceGroupsTable = ({ transactionAccountId }: InvoiceGroupsTableProps) => {
  const { data, loading, error } = useQuery<
    FetchInvoiceGroupsForTransactionAccountQuery,
    FetchInvoiceGroupsForTransactionAccountQueryVariables
  >(fetchInvoiceGroupsForTransactionAccount, {
    variables: {
      id: transactionAccountId ?? '',
    },
    skip: !transactionAccountId,
  });

  const getInvoiceGroupName = (name?: string | null | undefined) => {
    if (!name || name?.toLowerCase() === 'none') {
      return 'Unnamed invoice group';
    }
    return name;
  };

  const getPurchanseOrder = (purchaseOrder?: InvoiceGroupPurchaseOrder | null) => {
    if (!purchaseOrder) {
      return <div>{'N/A'}</div>;
    }
    return (
      <>
        <div>{purchaseOrder.number}</div>
        <div>({purchaseOrder.oneTimeUse ? 'Single-use' : 'Multi-use'})</div>
      </>
    );
  };

  const hasNoResults = !loading && (!data || data?.transactionAccount.availableInvoiceGroups?.totalCount === 0);
  const invoiceGroups = data?.transactionAccount.availableInvoiceGroups?.nodes;

  // TODO : FetchMore - current behavior is to fetch all. Need to understand pagination from CCP
  return (
    <SearchResultsTable
      id="invoice-groups"
      isLoading={loading}
      error={error?.toString()}
      noResults={hasNoResults}
      heading={'Invoice groups'}
      entityName="invoice groups"
    >
      <ColGroup columns={columns} />
      <TableHead columns={columns} />
      <TableBody>
        {(invoiceGroups ?? []).map((invoiceGroup, index) => {
          return (
            <tr key={`search-result-${index}`}>
              <td>
                {invoiceGroup?.id ? (
                  <Link
                    to={`${INVOICE_GROUP_PATH}/${encodeURIComponent(invoiceGroup?.id)}`}
                    onClick={(e) => e.stopPropagation()}
                    className="avatar-container"
                    data-testid="click"
                  >
                    {/*// @ts-ignore */}
                    {<img src={InvoiceGroupIcon} className="avatar medium not-round" />}
                    {getInvoiceGroupName(invoiceGroup?.name)}
                  </Link>
                ) : (
                  'Not available'
                )}
              </td>
              <td>
                <BillingAddressDetailsLine
                  address={invoiceGroup?.shipToParty}
                  noAddressLabel="No default shipping address"
                />
              </td>
              <td>
                <PaymentMethodSelectDetailsLine paymentMethod={invoiceGroup?.defaultPaymentMethod} />
              </td>
              <SimpleTD text={invoiceGroup?.currency} />
              {/*// @ts-ignore */}
              <SimpleTD text={getPurchanseOrder(invoiceGroup?.purchaseOrder)} />
              <td />
            </tr>
          );
        })}
      </TableBody>
    </SearchResultsTable>
  );
};

export default InvoiceGroupsTable;
